/**
 * One compact toolbar row: scope controls collapsed behind a trigger, saved views,
 * and a summary chip of what is currently applied.
 *
 * Why collapsed: the scope block used to occupy a full card of vertical space above
 * the fold on an analytics page whose job is a fast overview. It is set rarely and
 * read often, so the READ (a chip saying "01.09. - 09.09. · Automatisch") stays
 * visible and the CONTROLS live one click away - the same trade the heatmap page
 * makes with its filter modal.
 */

import { useEffect, useRef, useState } from "react";
import { HiCalendar, HiCheck, HiChevronDown, HiBookmark, HiTrash, HiStar, HiPlus } from "react-icons/hi";
import type { Grain } from "../../services/sales-metrics";
import type { MetricsSavedView } from "../../services/metrics-presets";
import { DATE_PRESETS, grainFits, type MetricsFilter } from "./scope";

function grainLabel(grain: Grain): string {
  return grain === "auto"
    ? "Automatisch"
    : grain === "day"
      ? "Täglich"
      : grain === "week"
        ? "Wöchentlich"
        : "Monatlich";
}

/** "01.09.2026 – 09.09.2026" */
function formatRange(filter: MetricsFilter): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };
  return `${fmt(filter.start)} – ${fmt(filter.end)}`;
}

/** Closes a popover on outside click and on Escape. */
function useDismiss(onDismiss: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onDismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onDismiss]);
  return ref;
}

export function MetricsToolbar({
  filter,
  onChange,
  stale,
  views,
  activeViewId,
  onSelectView,
  onSaveView,
  onDeleteView,
  onSetDefaultView,
  canSaveViews,
}: {
  filter: MetricsFilter;
  onChange: (next: MetricsFilter) => void;
  stale?: boolean;
  views: MetricsSavedView[];
  activeViewId: string | null;
  onSelectView: (view: MetricsSavedView) => void;
  onSaveView: (name: string, isDefault: boolean) => void;
  onDeleteView: (id: string) => void;
  onSetDefaultView: (id: string) => void;
  canSaveViews: boolean;
}) {
  const [scopeOpen, setScopeOpen] = useState(false);
  const [viewsOpen, setViewsOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState("");

  const scopeRef = useDismiss(() => setScopeOpen(false));
  const viewsRef = useDismiss(() => {
    setViewsOpen(false);
    setNaming(false);
  });

  const activePreset = DATE_PRESETS.find((p) => {
    const r = p.resolve();
    return r.start === filter.start && r.end === filter.end;
  });

  const setCustom = (key: "start" | "end", value: string) => {
    if (!value) return;
    const next = { ...filter, [key]: value };
    if (next.start > next.end) {
      if (key === "start") next.end = value;
      else next.start = value;
    }
    if (!grainFits(next.start, next.end, next.grain)) next.grain = "auto";
    onChange(next);
  };

  const activeView = views.find((v) => v.id === activeViewId) ?? null;

  const triggerClass =
    "inline-flex h-9 items-center gap-1.5 rounded-lg bg-white px-3 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-gray-300 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:ring-gray-600 dark:hover:bg-gray-600";

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* ---- Scope: label reads the current range, controls open on click ---- */}
      <div className="relative" ref={scopeRef}>
        <button
          type="button"
          onClick={() => setScopeOpen((v) => !v)}
          className={triggerClass}
          aria-expanded={scopeOpen}
          aria-haspopup="dialog"
        >
          <HiCalendar className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
          <span>{activePreset?.label ?? formatRange(filter)}</span>
          <span className="text-gray-400 dark:text-gray-500">·</span>
          <span className="font-normal text-gray-500 dark:text-gray-400">
            {grainLabel(filter.grain)}
          </span>
          <HiChevronDown
            className={`h-4 w-4 transition-transform ${scopeOpen ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>

        {scopeOpen ? (
          <div
            role="dialog"
            aria-label="Zeitraum wählen"
            className="absolute left-0 z-30 mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-lg bg-white p-3 shadow-lg ring-1 ring-gray-300 dark:bg-gray-800 dark:ring-gray-600 sm:left-auto sm:right-0"
          >
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Zeitraum
            </p>
            <div className="mb-3 grid grid-cols-2 gap-1">
              {DATE_PRESETS.map((preset) => {
                const isActive = preset.id === activePreset?.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => onChange(preset.resolve())}
                    aria-pressed={isActive}
                    className={`rounded-md px-2 py-1.5 text-left text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isActive
                        ? "bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>

            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Eigener Zeitraum
            </p>
            <div className="mb-3 flex items-center gap-1.5">
              <input
                type="date"
                value={filter.start}
                max={filter.end}
                onChange={(e) => setCustom("start", e.target.value)}
                aria-label="Startdatum"
                className="h-9 min-w-0 flex-1 rounded-lg bg-white px-2 text-xs text-gray-900 ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
              />
              <span className="text-gray-400">–</span>
              <input
                type="date"
                value={filter.end}
                min={filter.start}
                onChange={(e) => setCustom("end", e.target.value)}
                aria-label="Enddatum"
                className="h-9 min-w-0 flex-1 rounded-lg bg-white px-2 text-xs text-gray-900 ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
              />
            </div>

            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Auflösung
            </p>
            <div className="flex flex-wrap gap-1">
              {(["auto", "day", "week", "month"] as Grain[]).map((g) => {
                // Past the server's bucket cap the RPC errors rather than truncating,
                // so an impossible grain is shown disabled with the reason.
                const fits = grainFits(filter.start, filter.end, g);
                const isActive = filter.grain === g;
                return (
                  <button
                    key={g}
                    type="button"
                    disabled={!fits}
                    title={fits ? undefined : "Für diesen Zeitraum zu fein"}
                    onClick={() => fits && onChange({ ...filter, grain: g })}
                    aria-pressed={isActive}
                    className={`rounded-md px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                      isActive
                        ? "bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-200"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    }`}
                  >
                    {grainLabel(g)}
                    {!fits ? " (zu fein)" : ""}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      {/* ---- Saved views ---- */}
      <div className="relative" ref={viewsRef}>
        <button
          type="button"
          onClick={() => setViewsOpen((v) => !v)}
          className={triggerClass}
          aria-expanded={viewsOpen}
          aria-haspopup="menu"
        >
          <HiBookmark className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />
          <span>{activeView ? activeView.name : "Ansichten"}</span>
          <HiChevronDown
            className={`h-4 w-4 transition-transform ${viewsOpen ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>

        {viewsOpen ? (
          <div
            role="menu"
            className="absolute left-0 z-30 mt-2 w-[280px] max-w-[calc(100vw-2rem)] rounded-lg bg-white py-1 shadow-lg ring-1 ring-gray-300 dark:bg-gray-800 dark:ring-gray-600 sm:left-auto sm:right-0"
          >
            {views.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                Noch keine Ansicht gespeichert.
              </p>
            ) : (
              views.map((view) => (
                <div
                  key={view.id}
                  className="group flex items-center gap-1 px-1 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <button
                    type="button"
                    onClick={() => {
                      onSelectView(view);
                      setViewsOpen(false);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left text-sm text-gray-900 dark:text-white"
                  >
                    <HiCheck
                      className={`h-4 w-4 shrink-0 ${view.id === activeViewId ? "" : "invisible"}`}
                      aria-hidden={view.id !== activeViewId}
                    />
                    <span className="min-w-0 flex-1 truncate">{view.name}</span>
                    {view.isDefault ? (
                      <HiStar
                        className="h-3.5 w-3.5 shrink-0 text-yellow-500"
                        title="Standardansicht"
                        aria-label="Standardansicht"
                      />
                    ) : null}
                  </button>
                  <button
                    type="button"
                    onClick={() => onSetDefaultView(view.id)}
                    className="rounded p-1 text-gray-400 opacity-0 hover:text-yellow-500 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500 group-hover:opacity-100"
                    title="Als Standard setzen"
                    aria-label={`${view.name} als Standard setzen`}
                  >
                    <HiStar className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteView(view.id)}
                    className="mr-1 rounded p-1 text-gray-400 opacity-0 hover:text-red-500 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500 group-hover:opacity-100"
                    title="Ansicht löschen"
                    aria-label={`${view.name} löschen`}
                  >
                    <HiTrash className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}

            {canSaveViews ? (
              <>
                <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
                {naming ? (
                  <form
                    className="flex items-center gap-1 px-2 py-1.5"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const name = draftName.trim();
                      if (!name) return;
                      onSaveView(name, false);
                      setDraftName("");
                      setNaming(false);
                      setViewsOpen(false);
                    }}
                  >
                    <input
                      autoFocus
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      placeholder="Name der Ansicht"
                      aria-label="Name der Ansicht"
                      className="h-8 min-w-0 flex-1 rounded-md bg-white px-2 text-xs text-gray-900 ring-1 ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:ring-gray-600"
                    />
                    <button
                      type="submit"
                      className="h-8 shrink-0 rounded-md bg-blue-600 px-2 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Speichern
                    </button>
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => setNaming(true)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <HiPlus className="h-4 w-4" aria-hidden="true" />
                    Aktuelle Ansicht speichern
                  </button>
                )}
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {stale ? (
        <span className="text-xs text-gray-500 dark:text-gray-400">aktualisiert…</span>
      ) : null}
    </div>
  );
}
